import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, unref as _unref } from "vue"

const _hoisted_1 = { style: {"margin":"0 auto"} }
const _hoisted_2 = { class: "position-relative justify-center flex" }
const _hoisted_3 = ["src"]
const _hoisted_4 = {
  key: 1,
  class: "shadow-9 fallback-image-container",
  style: {"max-width":"400px","width":"100%","border-radius":"16px","aspect-ratio":"1/1","background":"linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)","display":"flex","flex-direction":"column","align-items":"center","justify-content":"center","border":"2px dashed #ccc"}
}
const _hoisted_5 = { class: "flex justify-center" }

import { ref, computed, onMounted } from 'vue'
import { useQuasar } from 'quasar'
import { useShareDialogs } from './utils/useShareDialogs'

interface Props {
  advertUuid: string
  advertSeoSlug?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ShareQrCodeImage',
  props: {
    advertUuid: {},
    advertSeoSlug: {}
  },
  emits: ["downloadStarted"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

const $q = useQuasar()
const { loadingImageDownload, downloadImage } = useShareDialogs()

const imageLoading = ref(true)
const imageError = ref(false)
const apiBaseUrl = computed(() => process.env.API_BASE || 'https://api.campertrader.de/v1')

/**
 * Builds a proper URL by ensuring there are no double slashes between base and path
 * @param base - The base URL
 * @param path - The path to append
 * @returns A properly formatted URL
 */
function buildUrl(base: string, path: string): string {
  // Remove trailing slash from base if it exists
  const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base
  // Remove leading slash from path if it exists
  const cleanPath = path.startsWith('/') ? path.slice(1) : path
  // Join with a single slash
  return `${cleanBase}/${cleanPath}`
}

// Helper function to get the QR code share image URL
function getQrCodeImageUrl(): string {
  if (!props.advertUuid) {
    return ''
  }
  return buildUrl(apiBaseUrl.value, `qr-code/${props.advertUuid}/`)
}

/**
 * Opens the QR code share image in a new tab as a fallback when embedded image fails
 */
function openImageInNewTab() {
  if (!props.advertUuid) {
    $q.notify({
      type: 'negative',
      message: 'Anzeige nicht verfügbar'
    })
    return
  }

  const imageUrl = getQrCodeImageUrl()
  window.open(imageUrl, '_blank')
}

/**
 * Preloads the image in the background and updates loading state when complete
 * Uses fetch() first to validate the image exists, then creates an Image object for preloading
 */
async function preloadQrCodeShareImage(): Promise<void> {
  if (!props.advertUuid) {
    imageLoading.value = false
    imageError.value = true
    return
  }

  const imageUrl = getQrCodeImageUrl()
  console.log('Starting QR code share image preload for URL:', imageUrl)

  try {
    // First, validate the image exists using fetch (same method as downloadImage)
    console.log('Validating QR code share image availability with fetch...')
    const response = await fetch(imageUrl, { method: 'HEAD' })

    console.log('Fetch response status:', response.status, response.statusText)
    console.log('Response content-type:', response.headers.get('content-type'))
    console.log('Response content-length:', response.headers.get('content-length'))

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    // If fetch succeeds, create an Image object for actual preloading
    console.log('QR code share image validation successful, starting preload...')
    const img = new Image()

    img.onload = () => {
      console.log('QR code share image preloaded successfully:', imageUrl)
      console.log('Image dimensions:', img.naturalWidth, 'x', img.naturalHeight)
      imageLoading.value = false
    }

    img.onerror = (event) => {
      console.error('QR code share image preload failed after successful fetch:', imageUrl)
      console.error('Error event details:', event)

      // Since fetch succeeded but Image() failed, this is likely a CORS issue
      console.warn('Continuing anyway - image should work in img tag despite preload failure')
      imageLoading.value = false
      // Don't set imageError to true since fetch validated the image exists
    }

    // Set the source to start loading
    img.src = imageUrl

  } catch (error) {
    console.error('QR code share image validation failed:', error)
    console.error('Error details:', {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    })
    console.error('Network state:', navigator.onLine ? 'online' : 'offline')
    console.error('API Base URL:', apiBaseUrl.value)
    console.error('Advert UUID:', props.advertUuid)

    imageLoading.value = false
    imageError.value = true
    $q.notify({
      type: 'negative',
      message: 'Das QR-Code Share Image konnte nicht geladen werden'
    })
  }
}

function onImageLoaded() {
  console.log('QR code share image load event fired from img tag')
  // Primary loading is handled by preloadQrCodeShareImage, this is just a backup
  imageLoading.value = false
}

function onImageError() {
  const imageUrl = getQrCodeImageUrl()
  console.error('QR code share image load error from img tag:', imageUrl)
  console.error('Network state:', navigator.onLine ? 'online' : 'offline')
  console.error('API Base URL:', apiBaseUrl.value)
  console.error('Advert UUID:', props.advertUuid)
  console.error('Current location:', window.location.href)

  imageLoading.value = false
  imageError.value = true
  $q.notify({
    type: 'negative',
    message: 'Das QR-Code Share Image konnte nicht geladen werden'
  })
}

function handleDownloadImage() {
  if (!props.advertSeoSlug && !props.advertUuid) {
    $q.notify({
      type: 'negative',
      message: 'Anzeige nicht verfügbar'
    })
    return
  }

  const imageUrl = getQrCodeImageUrl()
  const fileName = `${props.advertSeoSlug || `qr-code-${props.advertUuid}`}.png`

  downloadImage({
    imageUrl,
    fileName,
    shareTitle: 'Heruntergeladener QR-Code',
    shareText: 'Bild öffnen mit...',
    successMessage: 'QR-Code in Galerie gespeichert',
    errorMessage: 'QR-Code Share Image konnte nicht heruntergeladen werden',
    onDownloadStarted: () => emit('downloadStarted')
  })
}

// Start preloading when component mounts
onMounted(() => {
  preloadQrCodeShareImage()
})

return (_ctx: any,_cache: any) => {
  const _component_q_skeleton = _resolveComponent("q-skeleton")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_btn = _resolveComponent("q-btn")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "text-h3 q-mb-lg text-center text-bold" }, "QR-Code teilen!", -1)),
    _createElementVNode("div", _hoisted_2, [
      (imageLoading.value)
        ? (_openBlock(), _createBlock(_component_q_skeleton, {
            key: 0,
            type: "rect",
            class: "shadow-9 q-mb-sm full-width",
            style: {"max-width":"400px","border-radius":"16px","aspect-ratio":"1/1"}
          }))
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("img", {
        src: getQrCodeImageUrl(),
        alt: "QR Code Share Image",
        class: "shadow-9 full-width",
        style: {"max-width":"400px","border-radius":"16px","aspect-ratio":"1/1","object-fit":"cover","cursor":"pointer"},
        onLoad: onImageLoaded,
        onError: onImageError,
        onClick: handleDownloadImage
      }, null, 40, _hoisted_3), [
        [_vShow, !imageLoading.value && !imageError.value]
      ]),
      (!imageLoading.value && imageError.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(_component_q_icon, {
              name: "fas fa-qrcode",
              size: "48px",
              color: "grey-6",
              class: "q-mb-md"
            }),
            _cache[1] || (_cache[1] = _createElementVNode("div", { class: "text-h6 text-grey-7 q-mb-sm" }, "QR-Code Share Image", -1)),
            _cache[2] || (_cache[2] = _createElementVNode("div", { class: "text-body2 text-grey-6 text-center q-px-md" }, " Das QR-Code-Bild konnte nicht geladen werden ", -1)),
            _createVNode(_component_q_btn, {
              flat: "",
              dense: "",
              color: "primary",
              class: "q-mt-md",
              onClick: openImageInNewTab
            }, {
              default: _withCtx(() => [
                _createVNode(_component_q_icon, {
                  name: "fas fa-external-link-alt",
                  class: "q-mr-xs"
                }),
                _cache[0] || (_cache[0] = _createTextVNode(" Bild öffnen ", -1))
              ]),
              _: 1,
              __: [0]
            })
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(_component_q_btn, {
        loading: _unref(loadingImageDownload),
        unelevated: "",
        color: "grey-10",
        "no-caps": "",
        flat: "",
        class: "q-mt-sm",
        disable: false,
        onClick: handleDownloadImage
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_icon, {
            size: "0.7rem",
            name: "fas fa-download",
            class: "q-mr-sm"
          }),
          _cache[3] || (_cache[3] = _createElementVNode("span", null, "Bild speichern", -1))
        ]),
        _: 1,
        __: [3]
      }, 8, ["loading"])
    ])
  ]))
}
}

})